import React from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { getRecoil } from "recoil-nexus";
import organizationIdState from "../atoms/organizationIdSelector";

const COLLECTION = "progressPhotoGroupReviews";

/**
 * Live coach notes for a progress photo group (by client + groupId).
 * Path: organizations/{org}/clients/{clientId}/progressPhotoGroupReviews/{groupId}
 */
export function useProgressPhotoGroupReview(clientId, groupId) {
  const [coachNotes, setCoachNotes] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!clientId || !groupId) {
      setCoachNotes("");
      setLoading(false);
      return;
    }
    const organizationId = getRecoil(organizationIdState);
    if (!organizationId) {
      setLoading(false);
      return;
    }

    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("clients")
      .doc(clientId)
      .collection(COLLECTION)
      .doc(groupId);

    const unsub = ref.onSnapshot(
      (snap) => {
        const data = snap.exists ? snap.data() : null;
        setCoachNotes(typeof data?.coachNotes === "string" ? data.coachNotes : "");
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error("progressPhotoGroupReviews subscription:", err);
        setError(err.message || "Failed to load notes");
        setLoading(false);
      },
    );

    return () => unsub();
  }, [clientId, groupId]);

  const saveNotes = React.useCallback(
    async (nextText) => {
      if (!clientId || !groupId) return;
      const organizationId = getRecoil(organizationIdState);
      if (!organizationId) return;

      setSaving(true);
      setError(null);
      try {
        const uid = firebase.auth()?.currentUser?.uid || null;
        const ref = firebase
          .firestore()
          .collection("organizations")
          .doc(organizationId)
          .collection("clients")
          .doc(clientId)
          .collection(COLLECTION)
          .doc(groupId);

        await ref.set(
          {
            coachNotes: nextText ?? "",
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
            updatedByUid: uid,
            groupId,
            clientId,
          },
          { merge: true },
        );
      } catch (err) {
        console.error("save progress photo notes:", err);
        setError(err.message || "Save failed");
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [clientId, groupId],
  );

  return { coachNotes, loading, saving, error, saveNotes };
}

export default useProgressPhotoGroupReview;
